import React, { useState } from "react";
import {
  Box,
  Paper,
  Button,
  Typography,
  IconButton,
  Chip,
  Autocomplete,
  TextField,
  Popover,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Slider,
  Divider,
  Grid,
  Stack,
  Badge,
  Tooltip,
  Menu,
  MenuItem,
  ListItemText,
  ListItemIcon,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { useEffect } from "react";
import { useSearchContext } from "../context/SearchContext";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";
import cities from "../data/cities.json";
import DatePickerPopover from "./DatePickerPopover";
import PassengerMenu from "./PassengerMenu";
import {
  TRIP_TYPES,
  DEFAULT_INITIAL_VALUES,
} from "../constants/flightSearchConstants";
import {
  formatDate,
  generateCalendarDates,
  generatePriceForDate,
  getPriceColor,
  getPriceBackgroundColor,
  getPassengerDisplayText,
} from "../utils/flightSearchUtils";
import Popper from "@mui/material/Popper";

export default function CompactSearchBar(props) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isSwapRotating, setIsSwapRotating] = useState(false);
  const {
    from,
    setFrom,
    to,
    setTo,
    departureDate,
    setDepartureDate,
    returnDate,
    setReturnDate,
    tripType,
    setTripType,
    adults,
    setAdults,
    students,
    setStudents,
    seniors,
    setSeniors,
    youths,
    setYouths,
    childrenCount,
    setChildrenCount,
    toddlers,
    setToddlers,
    infants,
    setInfants,
    cabin,
    setCabin,
    carryOnBags,
    setCarryOnBags,
    checkedBags,
    setCheckedBags,
    directOnly,
    setDirectOnly,
    compareKayak,
    setCompareKayak,
    includeSouthwest,
    setIncludeSouthwest,
    fromFieldOpen,
    setFromFieldOpen,
    toFieldOpen,
    setToFieldOpen,
    datePopoverAnchor,
    setDatePopoverAnchor,
    activeDateField,
    setActiveDateField,
    currentMonth,
    setCurrentMonth,
    dateTab,
    setDateTab,
    departureExact,
    setDepartureExact,
    returnExact,
    setReturnExact,
    activeSegmentId,
    setActiveSegmentId,
    searchBarRef,
  } = useSearchContext();

  // Extract isResultPage from props
  const { isResultPage = false, ...otherProps } = props;

  // Local state for dropdowns and popups
  const [passengersAnchorEl, setPassengersAnchorEl] = useState(null);
  const [tripTypeAnchorEl, setTripTypeAnchorEl] = useState(null);
  const [bagsAnchorEl, setBagsAnchorEl] = useState(null);
  const [currentMonthLocal, setCurrentMonthLocal] = useState(new Date());
  const [dateTabLocal, setDateTabLocal] = useState(0);
  const [departureExactLocal, setDepartureExactLocal] = useState("exact");
  const [returnExactLocal, setReturnExactLocal] = useState("exact");
  const [activeDateFieldLocal, setActiveDateFieldLocal] = useState(null);
  const [datePopoverAnchorLocal, setDatePopoverAnchorLocal] = useState(null);
  const searchBarRefLocal = React.useRef(null);
  const fromInputRef = React.useRef(null);
  const toInputRef = React.useRef(null);
  const compactDepartureDateRef = React.useRef(null);
  const compactReturnDateRef = React.useRef(null);
  const compactTravelersRef = React.useRef(null);
  const expandedTravelersRef = React.useRef(null);
  const expandedFromAnchorRef = React.useRef(null);
  const expandedToAnchorRef = React.useRef(null);
  const [pendingOpenPassengers, setPendingOpenPassengers] = useState(false);
  const [pendingOpenDateField, setPendingOpenDateField] = useState(null); // 'departure' | 'return' | null
  const expandedDepartureDateRef = React.useRef(null);
  const expandedReturnDateRef = React.useRef(null);
  
  // New state for additional checkboxes
  const [includeEmirates, setIncludeEmirates] = useState(true); // Pre-checked Emirates
  const [includePriceline, setIncludePriceline] = useState(false);

  // Add toQuery state for curated design
  const [toQuery, setToQuery] = useState("");
  const [fromQuery, setFromQuery] = useState("");
  const [isFromPlusClick, setIsFromPlusClick] = useState(false);
  const [isToPlusClick, setIsToPlusClick] = useState(false);

  // Reset plus click flags when fields are closed
  useEffect(() => {
    if (!fromFieldOpen) {
      setIsFromPlusClick(false);
    }
  }, [fromFieldOpen]);

  useEffect(() => {
    if (!toFieldOpen) {
      setIsToPlusClick(false);
    }
  }, [toFieldOpen]);

  // Helper function to check if more than 2 locations are selected
  const hasMoreThanTwoLocations = () => {
    const totalLocations = (from && from.length > 0 ? from.length : 0) + (to && to.length > 0 ? to.length : 0);
    return totalLocations > 2;
  };

  // Travelers display text
  const totalTravelers = adults + students + seniors + youths + childrenCount + toddlers + infants;
  const travelersText = totalTravelers === 1 && adults === 1 ? '1 adult' : `${totalTravelers} travelers`;

  // Default filter function for autocomplete
  const defaultFilter = (options, params) => {
    const filtered = options.filter((option) => {
      const searchTerm = params.inputValue.toLowerCase();
      return (
        option.city.toLowerCase().includes(searchTerm) ||
        option.code.toLowerCase().includes(searchTerm) ||
        option.country.toLowerCase().includes(searchTerm) ||
        (option.name && option.name.toLowerCase().includes(searchTerm))
      );
    });
    return filtered;
  };

  // Helper function to group and sort cities for autocomplete
  const getGroupedCities = () => {
    // Group cities by metro area
    const metroGroups = {};
    const singleCities = [];

    cities.forEach((city) => {
      if (city.isMetroOnly) {
        metroGroups[city.code] = {
          metro: city,
          airports: [],
        };
      } else if (city.sub && city.parentMetro) {
        if (!metroGroups[city.parentMetro]) {
          metroGroups[city.parentMetro] = {
            metro: cities.find((c) => c.code === city.parentMetro),
            airports: [],
          };
        }
        metroGroups[city.parentMetro].airports.push(city);
      } else {
        singleCities.push(city);
      }
    });

    // Create grouped array
    const grouped = [];

    // Add metro areas with their airports
    Object.values(metroGroups).forEach((group) => {
      if (group.metro) {
        grouped.push(group.metro);
        group.airports.forEach((airport) => {
          grouped.push({ ...airport, sub: true });
        });
      }
    });

    // Add single cities
    grouped.push(...singleCities);

    return grouped;
  };

  // Function to close all dropdowns
  const closeAllDropdowns = () => {
    setFromFieldOpen(false);
    setToFieldOpen(false);
    setDatePopoverAnchorLocal(null);
    setActiveSegmentId(null);
    setActiveDateFieldLocal(null);
    setPassengersAnchorEl(null);
    setBagsAnchorEl(null);
    setTripTypeAnchorEl(null);
  };

  // Function to close date picker
  const closeDatePicker = () => {
    setDatePopoverAnchorLocal(null);
    setActiveDateFieldLocal(null);
  };

  // Function to handle date selection
  const handleDateSelect = (date) => {
    if (tripType === "One-way") {
      setDepartureDate(date);
      setDatePopoverAnchorLocal(null);
      setActiveDateFieldLocal(null);
     
    } else {
      if (activeDateFieldLocal === "departure") {
        setDepartureDate(date);
        setReturnDate("");
        setActiveDateFieldLocal("return");
        // Don't close the popover, switch to return selection
      } else if (activeDateFieldLocal === "return") {
        if (new Date(date) > new Date(departureDate)) {
          setReturnDate(date);
          setDatePopoverAnchorLocal(null);
        
        } else {
          setDepartureDate(date);
          setReturnDate("");
          setActiveDateFieldLocal("return");
        }
      } else {
        setDepartureDate(date);
        setReturnDate("");
        setActiveDateFieldLocal("return");
      }
    }
  };

  // Function to check if date is selected
  const isDateSelected = (date, currentDateTab = null) => {
    const d = new Date(date).setHours(0, 0, 0, 0);

    // In MONTH mode (dateTab === 2), only highlight the departure date
    if (currentDateTab === 2) {
      const dep = new Date(departureDate).setHours(0, 0, 0, 0);
      return d === dep && departureDate;
    }

    const dep = new Date(departureDate).setHours(0, 0, 0, 0);
    const ret = new Date(returnDate).setHours(0, 0, 0, 0);
    if (tripType === "One-way") return d === dep;
    return d === dep || d === ret;
  };

  // Function to check if date is in range
  const isDateInRange = (date, currentDateTab = null) => {
    // In MONTH mode (dateTab === 2), don't show date ranges
    if (currentDateTab === 2) return false;

    if (tripType === "One-way") return false;
    const d = new Date(date).setHours(0, 0, 0, 0);
    const dep = new Date(departureDate).setHours(0, 0, 0, 0);
    const ret = new Date(returnDate).setHours(0, 0, 0, 0);
    return dep && ret && d > dep && d < ret;
  };

  // Sync with props when they change (if provided)
  useEffect(() => {
    if (props.initialFrom && props.initialFrom !== from) {
      setFrom(props.initialFrom);
    }
  }, [props.initialFrom, from, setFrom]);

  useEffect(() => {
    if (props.initialTo && props.initialTo !== to) {
      setTo(props.initialTo);
    }
  }, [props.initialTo, to, setTo]);

  useEffect(() => {
    if (props.initialTripType && props.initialTripType !== tripType) {
      setTripType(props.initialTripType);
    }
  }, [props.initialTripType, tripType, setTripType]);

  // Add click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside the search bar and not on any dropdown
      const searchBarElement = searchBarRefLocal.current;
      const fromDropdown = document.querySelector(
        '[data-popper-placement="bottom-start"]'
      );
      const toDropdown = document.querySelector(
        '[data-popper-placement="bottom-start"]'
      );
      const passengerMenu = document.querySelector("[data-passenger-menu]");
      const datePopover = document.querySelector("[data-date-picker]");

      const isOutsideSearchBar =
        searchBarElement && !searchBarElement.contains(event.target);
      const isOutsideDropdowns =
        !fromDropdown?.contains(event.target) &&
        !toDropdown?.contains(event.target) &&
        !passengerMenu?.contains(event.target) &&
        !datePopover?.contains(event.target);

      if (isOutsideSearchBar && isOutsideDropdowns) {
        closeAllDropdowns();
      }
    };

    // Add event listener
    document.addEventListener("mousedown", handleClickOutside);

    // Cleanup
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      // Reset all dropdown states when component unmounts
      closeAllDropdowns();
    };
  }, []);

  // If user clicks travelers in compact mode, expand first, then anchor to expanded field
  useEffect(() => {
    if (isExpanded && pendingOpenPassengers) {
      setPassengersAnchorEl(expandedTravelersRef.current);
      setPendingOpenPassengers(false);
    }
  }, [isExpanded, pendingOpenPassengers]);

  // After expanding, anchor the date picker to the corresponding expanded field
  useEffect(() => {
    if (!isExpanded || !pendingOpenDateField) return;
    if (pendingOpenDateField === 'departure') {
      setDatePopoverAnchorLocal(expandedDepartureDateRef.current);
      setActiveDateFieldLocal('departure');
    } else if (pendingOpenDateField === 'return') {
      setDatePopoverAnchorLocal(expandedReturnDateRef.current);
      setActiveDateFieldLocal('return');
    }
    setPendingOpenDateField(null);
  }, [isExpanded, pendingOpenDateField]);

  // Cleanup effect to reset dropdown states when navigating
  useEffect(() => {
    return () => {
      // Reset dropdown states when component unmounts (e.g., when navigating)
      setFromFieldOpen(false);
      setToFieldOpen(false);
      setPassengersAnchorEl(null);
      setDatePopoverAnchorLocal(null);
      setActiveDateFieldLocal(null);
    };
  }, []);

  // Format dates for display
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const dayOfWeek = dayNames[date.getDay()];
    const month = date.getMonth() + 1;
    const day = date.getDate();
    return `${dayOfWeek} ${month}/${day}`;
  };

  // Common field styling
  const fieldStyle = {
    display: "flex",
    alignItems: "center",
    px: 2,
    py: 1,
    borderRadius: "8px",
    background: "#f0f3f5",
    border: "1px solid #d9e2e8",
    boxShadow: "none",
    fontWeight: 400,
    fontSize: "14px",
    lineHeight: "20px",
  };

  // Handle swap locations
  const handleSwap = (e) => {
    e.stopPropagation(); // Prevent expansion when clicking swap

    // Trigger rotation animation
    setIsSwapRotating((prev) => !prev);

    const tempFrom = [...from];
    const tempTo = [...to];

    // Swap the local state
    setFrom(tempTo);
    setTo(tempFrom);

    // Close dropdowns and clear inline queries
    setFromFieldOpen(false);
    setToFieldOpen(false);
    setFromQuery("");
    setToQuery("");

    // Update shared context
    // updateSearchState({
    //   from: tempTo,
    //   to: tempFrom,
    // });

    // Pass swapped values back to parent if onSwap callback is provided
    if (props.onSwap) {
      props.onSwap({
        from: tempTo,
        to: tempFrom,
      });
    }
  };

  // Handle field clicks to expand
  const handleFieldClick = (field) => {
    setIsExpanded(true);
    // Could pass which field was clicked for focus management
    if (props.onFieldClick) {
      props.onFieldClick(field);
    }
  };

  // Handle expand search
  const handleExpandSearch = () => {
    setIsExpanded(true);
  };

  // Handle submit
  const handleSubmit = () => {
    if (props.onSubmit) {
      const submitData = {
        from,
        to,
        departureDate,
        returnDate,
        adults,
        students,
        seniors,
        youths,
        childrenCount,
        toddlers,
        infants,
        bags: carryOnBags + checkedBags,
        tripType,
        cabin,
        directOnly,
        compareKayak,
        includeSouthwest,
      };
      props.onSubmit(submitData);
      setIsExpanded(false);
    }
  };

  // Date navigation functions
  const handleDateNavigation = (direction, dateType) => {
    const currentDate = dateType === "departure" ? departureDate : returnDate;
    if (!currentDate) return;

    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + direction);

    if (dateType === "departure") {
      setDepartureDate(newDate);
      // Only update both dates if it's the RIGHT arrow and dates are the same
      if (
        direction === 1 &&
        returnDate &&
        isSameDay(departureDate, returnDate)
      ) {
        setReturnDate(newDate);
      }
    } else {
      // For return date, only update return date
      setReturnDate(newDate);
    }
  };

  const isSameDay = (date1, date2) => {
    if (!date1 || !date2) return false;

    // Convert to Date objects if they are strings
    const d1 = typeof date1 === "string" ? new Date(date1) : date1;
    const d2 = typeof date2 === "string" ? new Date(date2) : date2;

    // Check if the dates are valid
    if (isNaN(d1.getTime()) || isNaN(d2.getTime())) return false;

    return (
      d1.getFullYear() === d2.getFullYear() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getDate() === d2.getDate()
    );
  };

  // Check if return date left arrow should be disabled
  const isReturnLeftArrowDisabled = () => {
    return returnDate && departureDate && isSameDay(departureDate, returnDate);
  };

  // Check if date picker is open
  const isDatePickerOpen = () => {
    return Boolean(datePopoverAnchorLocal);
  };

  // Compact header version - matching KAYAK's clean design
  if (!isExpanded) {
    return (
        <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          gap: 0.5,
          backgroundColor: "transparent",
        }}
      >
        {/* Trip Type Button */}
        <Button
          onClick={(e) => {
            e.stopPropagation();
            handleFieldClick("tripType");
          }}
          sx={{
            color: "#333",
            fontWeight: 500,
            fontSize: "14px",
            textTransform: "none",
            padding: "8px 12px",
            backgroundColor: "#f0f3f5",
            borderRadius: "8px",
            minWidth: "auto",
            border: "1px solid #e0e0e0",
            boxShadow: "none",
            whiteSpace: "nowrap",
            minHeight: "44px",
            "&:hover": {
              backgroundColor: "#d9e2e8",
              borderRadius: "8px",
            },
          }}
          ref={searchBarRefLocal}
        >
          {tripType}
        </Button>

        {/* From/To Container */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            backgroundColor: "#f0f3f5",
            borderRadius: "8px",
            border: "1px solid #d9e2e8",
            maxWidth: "300px",
          }}
        >
          {/* From Field */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flex: 1,
              minWidth: 0,
              "&:hover": {
                backgroundColor: "#d9e2e8",
                borderRadius: "8px",
              },
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleFieldClick("from");
              if (!isExpanded) {
                setFromFieldOpen(true);
              }
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flex: 1,
                minWidth: 0,
                padding: "8px 12px",
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {from.length > 0 ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 0.5,
                      alignItems: "center",
                    }}
                  >
                    {from.length === 1 ? (
                      // Single location - show full name
                      <Typography
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          backgroundColor: "#fff",
                          borderRadius: "2px",
                          padding: "1.5px 8px",
                          border: "1px solid #e0e0e0",
                          fontSize: "14px",
                          fontWeight: 500,
                          color: "#333",
                          gap: 1,
                          minWidth: 0,
                          maxWidth: "180px",
                          "&:hover": {
                            borderColor: "#333333",
                          },
                        }}
                      >
                        <Typography
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            fontSize: "14px",
                            fontWeight: 500,
                            color: "#333",
                            flex: 1,
                            minWidth: 0,
                          }}
                        >
                          {from.length === 1
                            ? from[0]
                            : from[0].split("(")[1]?.replace(")", "") ||
                            from[0]}
                        </Typography>
                        <IconButton
                          size="small"
                          sx={{
                            width: "11px",
                            height: "11px",
                            border: "1px solid #d9e2e8",
                            borderRadius: "4px",
                            color: "#192024",
                            fill: "#192024",
                            p: 0,
                            minWidth: "11px",
                            flexShrink: 0,
                            "&:hover": {
                              backgroundColor: "rgba(0,0,0,0.1)",
                            },
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setFrom([]);
                          }}
                        >
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 200 200"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M180 37.918L162.082 20L100 82.208L37.918 20L20 37.918L82.208 100L20 162.082L37.918 180L100 117.792L162.082 180L180 162.082L117.792 100z"
                            />
                          </svg>
                        </IconButton>
                      </Typography>
                    ) : (
                      // Multiple locations - show comma-separated codes
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: 500,
                          backgroundColor: "#fff",
                          borderRadius: "2px",
                          padding: "1.5px 8px",
                          border: "1px solid #e0e0e0",
                          color: "#333",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          gap: 1,
                          minWidth: 0,
                          maxWidth: "190px",
                          "&:hover": {
                            borderColor: "#333333",
                          },
                        }}
                      >
                        {from
                          .map(
                            (city) =>
                              city.split("(")[1]?.replace(")", "") || city
                          )
                          .join(", ")}
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 500,
                      color: "#999",
                      padding: "4px 8px",
                    }}
                  >
                    From?
                  </Typography>
                )}
              </Box>
              {from.length > 0 && (
                <IconButton
                  size="small"
                  sx={{
                    width: "26px",
                    height: "26px",
                    color: "#666",
                    minWidth: "26px",
                    flexShrink: 0,
                    backgroundColor: "#ffffff",
                    border: "1px solid #e0e0e0",
                    borderRadius: "2px",
                    "&:hover": {
                      backgroundColor: "#ffffff",
                      borderColor: "#333333",
                    },
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleExpandSearch();
                    setFromFieldOpen(true);
                  }}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 200 200"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M107.5 92.5V30h-15v62.5H30v15h62.5V170h15v-62.5H170v-15z" />
                  </svg>
                </IconButton>
              )}
            </Box>
          </Box>

          {/* Swap Button */}
          <IconButton
            onClick={handleSwap}
            sx={{
              width: "32px",
              height: "32px",
              color: "#333333",
              minWidth: "32px",
              "&:hover": {
                backgroundColor: "#d9e2e8",
                borderRadius: "8px",
              },
            }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 200 200"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M56.238 154.801c-25.271-30.326-30.335-33.201-25-39.603l25-30l11.523 9.603L53.013 112.5H120v15H53.013l14.749 17.699l-11.524 9.602zm86.524-40l-11.523-9.603L145.987 87.5H80v-15h65.987l-14.749-17.699l11.523-9.603l25 30c5.335 6.403.272 9.278-24.999 39.603z" />
            </svg>
          </IconButton>

          {/* To Field */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flex: 1,
              minWidth: 0,
              "&:hover": {
                backgroundColor: "#d9e2e8",
                borderRadius: "8px",
              },
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleFieldClick("to");
              setToFieldOpen(true);
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flex: 1,
                minWidth: 0,
                padding: "8px 12px",
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {to.length > 0 ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 0.5,
                      alignItems: "center",
                    }}
                  >
                    {to.length === 1 ? (
                      // Single location - show full name
                      <Typography
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          backgroundColor: "#fff",
                          borderRadius: "2px",
                          padding: "1.5px 8px",
                          border: "1px solid #e0e0e0",
                          fontSize: "14px",
                          fontWeight: 500,
                          color: "#333",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Typography
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            fontSize: "14px",
                            fontWeight: 500,
                            color: "#333",
                            flex: 1,
                            minWidth: 0,
                          }}
                        >
                          {to.length === 1
                            ? to[0]
                            : to[0].split("(")[1]?.replace(")", "") || to[0]}
                        </Typography>
                        <IconButton
                          size="small"
                          sx={{
                            width: "11px",
                            height: "11px",
                            border: "1px solid #d9e2e8",
                            borderRadius: "4px",
                            color: "#192024",
                            fill: "#192024",
                            p: 0,
                            minWidth: "11px",
                            flexShrink: 0,
                            "&:hover": {
                              backgroundColor: "rgba(0,0,0,0.1)",
                            },
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setTo([]);
                          }}
                        >
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 200 200"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M180 37.918L162.082 20L100 82.208L37.918 20L20 37.918L82.208 100L20 162.082L37.918 180L100 117.792L162.082 180L180 162.082L117.792 100z"
                            />
                          </svg>
                        </IconButton>
                      </Typography>
                    ) : (
                      // Multiple locations - show comma-separated codes
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: 500,
                          backgroundColor: "#fff",
                          borderRadius: "2px",
                          padding: "1.5px 8px",
                          border: "1px solid #e0e0e0",
                          color: "#333",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          gap: 1,
                          minWidth: 0,
                          maxWidth: "190px",
                          "&:hover": {
                            borderColor: "#333333",
                          },
                        }}
                      >
                        {to
                          .map(
                            (city) =>
                              city.split("(")[1]?.replace(")", "") || city
                          )
                          .join(", ")}
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 500,
                      color: "#999",
                      padding: "4px 8px",
                    }}
                  >
                    To?
                  </Typography>
                )}
              </Box>

              {to.length > 0 && (
                <IconButton
                  size="small"
                  sx={{
                    width: "26px",
                    height: "26px",
                    color: "#666",
                    minWidth: "26px",
                    padding: "5px",
                    flexShrink: 0,
                    backgroundColor: "#ffffff",
                    border: "1px solid #e0e0e0",
                    borderRadius: "2px",
                    "&:hover": {
                      backgroundColor: "#ffffff",
                      borderColor: "#333333",
                    },
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleExpandSearch();
                    setToFieldOpen(true);
                  }}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 200 200"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M107.5 92.5V30h-15v62.5H30v15h62.5V170h15v-62.5H170v-15z" />
                  </svg>
                </IconButton>
              )}
            </Box>
          </Box>
        </Box>

        {/* Departure Date */}
        <Box
          onClick={(e) => {
            e.stopPropagation();
            handleFieldClick("dates");
            setFromFieldOpen(false);
            setToFieldOpen(false);
            setPassengersAnchorEl(null);
            if (!isExpanded) {
              setDatePopoverAnchorLocal(compactDepartureDateRef.current || searchBarRefLocal.current);
            } else {
              setDatePopoverAnchorLocal(expandedDepartureDateRef.current || searchBarRefLocal.current);
            }
            setActiveDateFieldLocal('departure');
            setPendingOpenDateField('departure');
          }}
          sx={{
            display: "flex",
            alignItems: "center",
            padding: "8px 12px",
            borderRadius: "8px",
            backgroundColor: "#f0f3f5",
            border: "1px solid #e0e0e0",
            cursor: "pointer",
            minHeight: "28px",
            whiteSpace: "nowrap",
            "&:hover": {
              backgroundColor: "#d9e2e8",
              borderRadius: "8px",
            },
          }}
          ref={compactDepartureDateRef}
        >
          <Typography sx={{ fontSize: "14px", fontWeight: 500, color: "#333" }}>
            {formatDate(departureDate)}
          </Typography>
          <Box
            sx={{
              display: "flex",
              ml: 1,
              visibility: isDatePickerOpen() ? "hidden" : "visible",
            }}
          >
            <IconButton
              size="small"
              sx={{ width: 16, height: 16, color: "#666", p: 0 }}
              onClick={(e) => {
                e.stopPropagation();
                handleDateNavigation(-1, "departure");
              }}
            >
              <svg
                viewBox="0 0 200 200"
                width="16"
                height="16"
                xmlns="http://www.w3.org/2000/svg"
                role="presentation"
              >
                <path
                  fill="#666"
                  d="M119.238 164.801c-53.425-64.11-55.449-63.064-50-69.603l50-60l11.523 9.603L84.763 100l45.999 55.199l-11.524 9.602z"
                />
              </svg>
            </IconButton>
            <IconButton
              size="small"
              sx={{ width: 16, height: 16, color: "#666", p: 0 }}
              onClick={(e) => {
                e.stopPropagation();
                handleDateNavigation(1, "departure");
              }}
            >
              <svg
                viewBox="0 0 200 200"
                width="16"
                height="16"
                xmlns="http://www.w3.org/2000/svg"
                role="presentation"
                style={{ transform: "rotate(180deg)" }}
              >
                <path
                  fill="#666"
                  d="M119.238 164.801c-53.425-64.11-55.449-63.064-50-69.603l50-60l11.523 9.603L84.763 100l45.999 55.199l-11.524 9.602z"
                />
              </svg>
            </IconButton>
          </Box>
        </Box>

        {/* Return Date - Only show if not One-way */}
        {tripType !== "One-way" && (
          <Box
            onClick={(e) => {
              e.stopPropagation();
              handleFieldClick("dates");
              if (!isExpanded) {
                setDatePopoverAnchorLocal(compactReturnDateRef.current || searchBarRefLocal.current);
              } else {
                setDatePopoverAnchorLocal(expandedReturnDateRef.current || searchBarRefLocal.current);
              }
              setActiveDateFieldLocal('return');
              setPendingOpenDateField('return');
            }}
            sx={{
              display: "flex",
              alignItems: "center",
              padding: "8px 12px",
              borderRadius: "8px",
              backgroundColor: "#f0f3f5",
              border: "1px solid #e0e0e0",
              cursor: "pointer",
              whiteSpace: "nowrap",
              minHeight: "28px",
              "&:hover": {
                backgroundColor: "#d9e2e8",
                borderRadius: "8px",
              },
            }}
            ref={compactReturnDateRef}
          >
            <Typography
              sx={{ fontSize: "14px", fontWeight: 500, color: "#333" }}
            >
              {formatDate(returnDate)}
            </Typography>
            <Box
              sx={{
                display: "flex",
                ml: 1,
                visibility: isDatePickerOpen() ? "hidden" : "visible",
              }}
            >
              <IconButton
                size="small"
                sx={{
                  width: 16,
                  height: 16,
                  color: isReturnLeftArrowDisabled() ? "#ccc" : "#333333",
                  p: 0,
                  cursor: isReturnLeftArrowDisabled()
                    ? "not-allowed"
                    : "pointer",
                }}
                disabled={isReturnLeftArrowDisabled()}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isReturnLeftArrowDisabled()) {
                    handleDateNavigation(-1, "return");
                  }
                }}
              >
                <svg
                  viewBox="0 0 200 200"
                  width="16"
                  height="16"
                  xmlns="http://www.w3.org/2000/svg"
                  role="presentation"
                >
                  <path
                    fill={isReturnLeftArrowDisabled() ? "#ccc" : "#333333"}
                    d="M119.238 164.801c-53.425-64.11-55.449-63.064-50-69.603l50-60l11.523 9.603L84.763 100l45.999 55.199l-11.524 9.602z"
                  />
                </svg>
              </IconButton>
              <IconButton
                size="small"
                sx={{ width: 16, height: 16, color: "#666", p: 0 }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDateNavigation(1, "return");
                }}
              >
                <svg
                  viewBox="0 0 200 200"
                  width="16"
                  height="16"
                  xmlns="http://www.w3.org/2000/svg"
                  role="presentation"
                  style={{ transform: "rotate(180deg)" }}
                >
                  <path
                    fill="#666"
                    d="M119.238 164.801c-53.425-64.11-55.449-63.064-50-69.603l50-60l11.523 9.603L84.763 100l45.999 55.199l-11.524 9.602z"
                  />
                </svg>
              </IconButton>
            </Box>
          </Box>
        )}

        {/* Travelers & Cabin */}
        <Box
          data-travelers-field
          onClick={(e) => {
            e.stopPropagation();
            closeAllDropdowns();
            setPendingOpenPassengers(true);
            handleFieldClick("passengers");
          }}
          sx={{
            display: "flex",
            alignItems: "center",
            padding: "8px 12px",
            borderRadius: "8px",
            backgroundColor: "#f0f3f5",
            border: "1px solid #e0e0e0",
            cursor: "pointer",
            whiteSpace: "nowrap",
            minHeight: "28px",
            "&:hover": {
              backgroundColor: "#d9e2e8",
              borderRadius: "8px",
            },
          }}
          ref={compactTravelersRef}
        >
          <Typography sx={{ fontSize: "14px", fontWeight: 500, color: "#333" }}>
            {travelersText}, {cabin}
          </Typography>
        </Box>

        {/* Search Button */}
        <Button
          variant="contained"
          onClick={handleSubmit}
          sx={{
            background: "linear-gradient(135deg, #ff690f 0%, #e8381b 100%)",
            borderRadius: "8px",
            minWidth: isResultPage ? "100px" : "40px",
            minHeight: isResultPage ? "40px" : "40px",
            boxShadow: "0 2px 8px #e0e0e0",
            textTransform: "none",
            minHeight: isResultPage ? "44px" : "40px",
            maxHeight: isResultPage ? "44px" : "40px",
            lineHeight: "16px !important",
            fontSize: "14px !important",
            padding: "8px 12px",
            minHeight: "44px",
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            gap: 1,
            "&:hover": {
              background: "#e65c00",
            },
          }}
        >
          <Box sx={{ color: "#fff" }}>
            <svg
              viewBox="0 0 200 200"
              width="20"
              height="20"
              xmlns="http://www.w3.org/2000/svg"
              role="presentation"
            >
              <path
                fill="#fff"
                d="M174.973 150.594l-29.406-29.406c5.794-9.945 9.171-21.482 9.171-33.819C154.737 50.164 124.573 20 87.368 20S20 50.164 20 87.368s30.164 67.368 67.368 67.368c12.345 0 23.874-3.377 33.827-9.171l29.406 29.406c6.703 6.703 17.667 6.703 24.371 0c6.704-6.702 6.704-17.674.001-24.377zM36.842 87.36c0-27.857 22.669-50.526 50.526-50.526s50.526 22.669 50.526 50.526s-22.669 50.526-50.526 50.526s-50.526-22.669-50.526-50.526z"
              />
            </svg>
          </Box>
          <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#fff" }}>
            {isResultPage ? "Update" : ""}
          </Typography>
        </Button>
      </Box>
    );
  }

  // Expanded sliding version
  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        right: 0,
        bottom: 0,
        width: "100vw",
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        zIndex: "1000 !important",
        display: "flex",
        justifyContent: "flex-end",
      }}
      onClick={() => {
        setIsExpanded(false);
        setFromFieldOpen(false);
        setToFieldOpen(false);
        setPassengersAnchorEl(null);
        setDatePopoverAnchorLocal(null);
        closeAllDropdowns();
      }}
    >
      <Box
        sx={{
          backgroundColor: "#fff",
          width: "100%",
          height: "fit-content",
          overflow: "hidden",
          zIndex: 1000,
          position: "relative",
          transform: isExpanded ? "translateX(0)" : "translateX(100%)",
          transition: "transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
          boxShadow: "-4px 0 20px rgba(0, 0, 0, 0.15)",
        }}
        onClick={(e) => {
          e.stopPropagation();
          // Close dropdowns when clicking on the expanded box content
          closeAllDropdowns();
        }}
      >
        {/* Header with Logo and Title */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 4,
            py: 2,
            backgroundColor: "#fff",
          }}
        >
          {/* KAYAK Logo and Title */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <svg
              width="125"
              height="24"
              viewBox="0 0 125 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              role="presentation"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M0 24h23.936V0H0v24zm25.266 0h23.936V0H25.266v24zm49.202 0H50.532V0h23.936v24zm1.33 0h23.936V0H75.798v24zM125 24h-23.936V0H125v24z"
                fill="#FF690F"
              ></path>
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M10.287 11.549V6H7.38v12h2.907v-5.548L13.348 18h3.208l-3.33-6 3.33-6h-3.208l-3.061 5.549zm24.785 4.724L34.552 18h-3.104l3.947-12h3.696l3.93 12h-3.194l-.52-1.727h-4.235zm2.117-6.837l-1.4 4.48h2.8l-1.4-4.48zM63.98 18h-2.906v-5.29L57.144 6h3.463l1.938 3.688L64.447 6h3.409l-3.876 6.71V18zm21.624-1.727L85.084 18h-3.105l3.948-12h3.696l3.93 12h-3.194l-.52-1.727h-4.235zm2.117-6.837l-1.4 4.48h2.8l-1.4-4.48zm23.63 2.113V6h-2.907v12h2.907v-5.548L114.412 18h3.208l-3.33-6 3.33-6h-3.208l-3.061 5.549z"
                fill="#FAFAFC"
              ></path>
            </svg>
          </Box>
        </Box>

        {/* Content Container */}
        <Box sx={{ px: 3, maxWidth: "1200px", margin: "0 auto" }}>
          {/* Trip Type Buttons */}
          <Box
            sx={{
              display: "flex",
              gap: 1,
              mb: 1,
              justifyContent: "flex-start",
            }}
          >
            {["Round-trip", "One-way", "Multi-city"].map((type) => (
              <Box
                key={type}
                sx={{
                  position: "relative",
                  display: "inline-block",
                }}
              >
                <input
                  type="radio"
                  id={type.toLowerCase().replace("-", "")}
                  name="tripType"
                  value={type}
                  checked={tripType === type}
                  style={{ display: "none" }}
                  onChange={(e) => setTripType(e.target.value)}
                />
                <label
                  htmlFor={type.toLowerCase().replace("-", "")}
                  style={{
                    display: "inline-block",
                    padding: "8px 16px",
                    fontSize: "14px",
                    fontWeight: 400,
                    color: tripType === type ? "#333" : "#666",
                    backgroundColor:
                      tripType === type ? "#f9fafb" : "transparent",
                    border:
                      tripType === type
                        ? "1px solid #192024"
                        : "1px solid #e0e0e0",
                    borderRadius: "8px",
                    cursor: "pointer",
                    textAlign: "center",
                    transition: "all 0.2s ease",
                    userSelect: "none",
                  }}
                >
                  {type}
                </label>
              </Box>
            ))}
          </Box>

          {/* Main Search Form */}
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            {/* From/To Container */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flex: 1,
                minWidth: "400px",
                minHeight: "52px",
              }}
            >
              {/* From Field */}
              <Box
                sx={{ flex: 1 }}
                onClick={(e) => {
                  e.stopPropagation();
                  closeAllDropdowns();
                  setFromFieldOpen(true);
                  setToFieldOpen(false); // Close To dropdown when From is clicked
                  // Auto-focus the text field immediately
                  setTimeout(() => {
                    try {
                      if (fromInputRef.current) {
                        fromInputRef.current.focus();
                      } else {
                        // Fallback: try to focus any input in the document
                        const inputs = document.querySelectorAll('input[placeholder="From?"]');
                        if (inputs.length > 0) {
                          inputs[0].focus();
                        }
                      }
                    } catch (error) {
                      console.log('Focus error:', error);
                    }
                  }, 50);
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }} ref={expandedFromAnchorRef}>
                  {from.length > 0 ? (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        padding: "0px 16px",
                        backgroundColor: "#f0f3f5",
                        borderRadius: "8px",
                        position: "relative",
                        height: "52px",
                        maxHeight: "52px",
                        border: fromFieldOpen ? "1px solid #333333" : "1px solid #d9e2e8",
                        "&:hover": {
                          backgroundColor: "#d9e2e8",
                        },
                      }}
                    >
                      {from.map((city, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            backgroundColor: "#fff",
                            borderRadius: "2px",
                            padding: "1.5px 8px",
                            border: "1px solid #e0e0e0",
                            fontSize: "14px",
                            fontWeight: 500,
                            color: "#333",
                            gap: 1,
                            minWidth: 0,
                            maxWidth: "180px",
                            "&:hover": {
                              borderColor: "#333333",
                            },
                          }}
                        >
                          <Typography
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              fontSize: "14px",
                              fontWeight: 500,
                              color: "#333",
                              flex: 1,
                              minWidth: 0,
                            }}
                          >
                            {from.length === 1
                              ? city
                              : city.split("(")[1]?.replace(")", "") || city}
                          </Typography>
                          <IconButton
                            size="small"
                            sx={{
                              width: "11px",
                              height: "11px",
                              border: "1px solid #d9e2e8",
                              borderRadius: "4px",
                              color: "#192024",
                              fill: "#192024",
                              p: 0,
                              minWidth: "11px",
                              flexShrink: 0,
                              "&:hover": {
                                backgroundColor: "rgba(0,0,0,0.1)",
                              },
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              const newFrom = from.filter(
                                (_, i) => i !== index
                              );
                              setFrom(newFrom);
                            }}
                          >
                            <svg
                              width="12"
                              height="12"
                              viewBox="0 0 200 200"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M180 37.918L162.082 20L100 82.208L37.918 20L20 37.918L82.208 100L20 162.082L37.918 180L100 117.792L162.082 180L180 162.082L117.792 100z"
                              />
                            </svg>
                          </IconButton>
                        </Box>
                      ))}
                      {from.length < 3 && (
                        <>
                          {/* Show text field when focused, when there's input, or when plus button is clicked */}
                          {(fromFieldOpen || fromQuery.length > 0 || isFromPlusClick) && (
                            <TextField
                              variant="outlined"
                              value={fromQuery}
                              inputRef={fromInputRef}

                              autoFocus={isExpanded ? fromFieldOpen : isFromPlusClick}
                                                                                           onFocus={() => {
                                // Don't open dropdown when plus button is clicked
                                if (!isFromPlusClick) {
                                  setFromFieldOpen(true);
                                  setFromQuery("");
                                }
                              }}
                              onClick={() => {
                                // Reset the plus click flag when clicking the field
                                if (isFromPlusClick) {
                                  setIsFromPlusClick(false);
                                }
                              }}
                              onBlur={() => {
                                // Keep field open if there's text, close if empty
                                if (fromQuery.length === 0) {
                                  setTimeout(() => setFromFieldOpen(false), 200);
                                }
                              }}
                              sx={{
                                minWidth: "120px",
                                maxWidth: "200px",
                                backgroundColor: "transparent",
                                "& .MuiOutlinedInput-root": {
                                  backgroundColor: "transparent",
                                  height: "26px",
                                  "& fieldset": { 
                                    border: "none",
                                    borderRadius: "2px",
                                  },
                                },
                                "& .MuiInputBase-input": {
                                  p: "4px 8px",
                                  fontSize: "14px",
                                  fontWeight: 500,
                                  color: "#333",
                                  "&::placeholder": {
                                    color: "#999",
                                    opacity: 1,
                                  },
                                },
                              }}
                              placeholder={isFromPlusClick ? "" : "From?"}
                                                             onChange={(e) => {
                                 setFromQuery(e.target.value);
                                 // Only open dropdown if not from plus button click
                                 if (!fromFieldOpen && !isFromPlusClick) setFromFieldOpen(true);
                               }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" && fromQuery.trim()) {
                                  // Handle Enter key to add custom location
                                  const newFrom = [...from, fromQuery.trim()];
                                  setFrom(newFrom);
                                  setFromQuery("");
                                  setFromFieldOpen(false);
                                }
                                if (e.key === "Escape") {
                                  setFromQuery("");
                                  setFromFieldOpen(false);
                                }
                              }}
                            />
                          )}
                          {/* Show plus button only when text field is not visible */}
                          {!fromFieldOpen && fromQuery.length === 0 && !isFromPlusClick && (
                            <IconButton
                              size="small"
                              sx={{
                                width: "26px",
                                height: "26px",
                                color: "#666",
                                minWidth: "26px",
                                flexShrink: 0,
                                backgroundColor: "#ffffff",
                                border: "1px solid #e0e0e0",
                                borderRadius: "2px",
                                "&:hover": {
                                  backgroundColor: "#ffffff",
                                  borderColor: "#333333",
                                },
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setIsFromPlusClick(true);
                                setFromFieldOpen(false); // Keep dropdown closed
                                setToFieldOpen(false); // Close To dropdown
                                setFromQuery("");
                                // Focus the text field after it's rendered
                                setTimeout(() => {
                                  if (fromInputRef.current) {
                                    fromInputRef.current.focus();
                                  } else {
                                    // Fallback: try to find by ID
                                    const inputs = document.getElementById('from-input');
                                    if (inputs) {
                                      inputs.focus();
                                    }
                                  }
                                }, 100);
                                
                                // Additional focus attempt with longer delay
                                setTimeout(() => {
                                  if (fromInputRef.current) {
                                    fromInputRef.current.focus();
                                  }
                                }, 200);
                              }}
                            >
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 200 200"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path d="M107.5 92.5V30h-15v62.5H30v15h62.5V170h15v-62.5H170v-15z" />
                              </svg>
                            </IconButton>
                          )}
                        </>
                      )}
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        padding: "0px 16px",
                        backgroundColor: "#f0f3f5",
                        borderRadius: "8px",
                        height: "52px",
                        maxHeight: "52px",
                        border: "1px solid #d9e2e8",
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor: "#d9e2e8",
                          borderColor: "#333333",
                        },
                      }}
                    >
                      <svg
                        viewBox="0 0 200 200"
                        width="1.25em"
                        height="1.25em"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{ marginRight: "8px", color: "#666" }}
                      >
                        <path d="M178.081 41.973c-2.681 2.663-16.065 17.416-28.956 30.221c0 107.916 3.558 99.815-14.555 117.807l-14.358-60.402l-14.67-14.572c-38.873 38.606-33.015 8.711-33.015 45.669c.037 8.071-3.373 13.38-8.263 18.237L50.66 148.39l-30.751-13.513c10.094-10.017 15.609-8.207 39.488-8.207c8.127-16.666 18.173-23.81 26.033-31.62L70.79 80.509L10 66.269c17.153-17.039 6.638-13.895 118.396-13.895c12.96-12.873 26.882-27.703 29.574-30.377c7.745-7.692 28.017-14.357 31.205-11.191c3.187 3.166-3.349 23.474-11.094 31.167zm-13.674 42.469l-8.099 8.027v23.58c17.508-17.55 21.963-17.767 8.099-31.607zm-48.125-47.923c-13.678-13.652-12.642-10.828-32.152 8.57h23.625l8.527-8.57z" />
                      </svg>
                      <TextField  
                        variant="outlined"
                        value={fromQuery}
                        autoFocus={fromFieldOpen}
                        onFocus={() => {
                          // Only open dropdown if NOT from plus button click
                          if (!isFromPlusClick) {
                            setFromFieldOpen(true);
                          }
                          // Reset the flag after focus
                          setTimeout(() => {
                            setIsFromPlusClick(false);
                          }, 200);
                        }}
                        sx={{
                          flex: 1,
                          backgroundColor: "transparent",
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "transparent",
                            px: 0,
                            '& fieldset': { border: 'none' },
                            '&:hover fieldset': { border: 'none' },
                            '&.Mui-focused fieldset': { border: 'none' },
                          },
                          "& .MuiInputBase-input": {
                            p: 0,
                            fontSize: "16px",
                            fontWeight: 500,
                            color: "#999",
                          },
                        }}
                        placeholder="From?"
                        onChange={(e) => {
                          const next = e.target.value;
                          setFromQuery(next);
                          if (!fromFieldOpen) setFromFieldOpen(true);
                        }}
                        inputRef={fromInputRef}
                        id="from-input"
                      />
                    </Box>
                  )}
                </Box>

                {/* Hidden Autocomplete for functionality */}
                <Box
                  sx={{
                    position: "absolute",
                    opacity: 0,
                    pointerEvents: "none",
                  }}
                >
                  <Autocomplete
                    options={getGroupedCities()}
                    getOptionLabel={(option) =>
                      option
                        ? option.displayname ||
                        `${option.city}, ${option.country} ${option.code}`
                        : ""
                    }
                    open={fromFieldOpen}
                    onOpen={() => {
                      closeAllDropdowns();
                      setFromFieldOpen(true);
                    }}
                    onClose={() => setFromFieldOpen(false)}
                    value={from.map(
                      (f) =>
                        getGroupedCities().find(
                          (c) => `${c.city} (${c.code})` === f
                        ) || null
                    )}
                    inputValue={fromQuery}
                    onInputChange={(_, val, reason) => {
                      if (reason === 'input') {
                        setFromQuery(val);
                      }
                    }}
                    filterOptions={(options) => defaultFilter(options, { inputValue: fromQuery })}
                    onChange={(_, newValue) => {
                      // Handle single selection from dropdown
                      if (newValue && newValue.length > 0) {
                        const lastSelected = newValue[newValue.length - 1];
                        if (lastSelected && lastSelected.city && lastSelected.code) {
                          const newFromValues = [...from, `${lastSelected.city} (${lastSelected.code})`];
                          setFrom(newFromValues);
                          setFromQuery("");
                          
                          // Close dropdown after selection
                          setFromFieldOpen(false);
                        }
                      } else {
                        setFromFieldOpen(false);
                      }
                    }}
                    renderOption={(props, option) => {
                      if (!option) return null; // Early return if option is null
                      const isSelected = from.some(
                        (f) => f === `${option.city} (${option.code})`
                      );
                      const isDisabled = !isSelected && from.length >= 3;
                      const isSubAirport = option.sub;
                      const showImageRow = from.length === 0;

                      const getCuratedImage = (code) => {
                        const imageMap = {
                          "NYC": "https://content.r9cdn.net/rimg/dimg/8c/a9/d1b21b20-city-15830-16eb6a60801.jpg?width=128&height=128&xhint=1887&yhint=913&outputtype=JPEG&crop=true",
                          "DEL": "https://content.r9cdn.net/rimg/dimg/6d/77/0cc45283-city-32821-176ddb032b7.jpg?width=128&height=128&xhint=1769&yhint=1931&outputtype=JPEG&crop=true",
                          "LAX": "https://content.r9cdn.net/rimg/dimg/a6/ae/d1a4a522-city-16078-190266aa26e.jpg?width=128&height=128&xhint=1430&yhint=902&outputtype=JPEG&crop=true",
                          "LAS": "https://content.r9cdn.net/rimg/dimg/0a/9e/073516d2-city-35107-1656d23229c.jpg?width=128&height=128&xhint=2420&yhint=1089&outputtype=JPEG&crop=true",
                          "HNL": "https://content.r9cdn.net/rimg/dimg/73/93/010fa630-city-28070-15b0fbf9066.jpg?width=128&height=128&xhint=1898&yhint=1299&outputtype=JPEG&crop=true"
                        };
                        return imageMap[code] || "https://via.placeholder.com/56";
                      };

                      return (
                        <li
                          onClick={(e) => {
                            e.stopPropagation();
                            setFromFieldOpen(false);
                          }}
                          {...props}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            padding: isSubAirport
                              ? "8px 12px 8px 48px"
                              : "12px",
                            borderBottom: "1px solid #f0f0f0",
                            opacity: isDisabled ? 0.5 : 1,
                            cursor: isDisabled ? "not-allowed" : "pointer",
                            backgroundColor: "#fff",
                          }}
                        >
                          <span
                            style={{
                              marginRight: 12,
                              color: "#666",
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            {isSubAirport && (
                              <SubdirectoryArrowRightIcon
                                sx={{
                                  fontSize: "28px",
                                  fontWeight: 900,
                                  color: "#333333",
                                }}
                              />
                            )}
                             <Box sx={{ display: "flex", alignItems: "center", gap: 1, backgroundColor: "#f0f3f5", fill: "#5a6872", p: 2, borderRadius: 2 }}>
                                <img
                                  src="/614.png"
                                  alt="airplane"
                                  style={{
                                    width: "24px",
                                    height: "24px",
                                    objectFit: "contain",
                                  }}
                                />
                              </Box>
                          </span>
                          <div style={{ flex: 1 }}>
                            <div
                              style={{
                                fontWeight: isSubAirport ? 400 : 600,
                                fontSize: "16px",
                                color: "#333",
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                const optionValue = `${option.city} (${option.code})`;
                                if (isSelected) {
                                  setFrom(
                                    from.filter((f) => f !== optionValue)
                                  );
                                } else if (from.length < 3) {
                                  setFrom([...from, optionValue]);
                                  setFromQuery("");
                                }
                                setFromFieldOpen(false);
                              }}
                            >
                              {isSubAirport
                                ? option.name
                                : `${option.city}, ${option.region || option.country
                                }, ${option.country}`}{" "}
                              <span style={{ color: "#666", fontWeight: 400 }}>
                                {option.code}
                              </span>
                            </div>
                            <div
                              style={{
                                fontSize: "14px",
                                color: "#666",
                                marginTop: 2,
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                const optionValue = `${option.city} (${option.code})`;
                                if (isSelected) {
                                  setFrom(
                                    from.filter((f) => f !== optionValue)
                                  );
                                } else if (from.length < 3) {
                                  setFrom([...from, optionValue]);
                                  setFromQuery("");
                                }
                                setFromFieldOpen(false);
                              }}
                            >
                              {isSubAirport
                                ? `${option.city}, ${option.region || option.country
                                }, ${option.country}`
                                : option.isMetroOnly
                                  ? "All airports"
                                  : option.name}
                            </div>
                          </div>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            disabled={isDisabled}
                            onChange={(e) => {
                              e.stopPropagation();
                              const optionValue = `${option.city} (${option.code})`;
                              if (isSelected) {
                                setFrom(from.filter((f) => f !== optionValue));
                              } else if (from.length < 3) {
                                setFrom([...from, optionValue]);
                                setFromQuery("");
                              }
                            }}
                            style={{
                              margin: 0,
                              width: "16px",
                              height: "16px",
                              cursor: isDisabled ? "not-allowed" : "pointer",
                            }}
                          />
                        </li>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="From?"
                        variant="outlined"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "#f0f3f5",
                            borderRadius: "8px",
                            height: "52px",
                            "& fieldset": {
                              border: "1px solid #d9e2e8",
                            },
                            "&:hover fieldset": {
                              borderColor: "#333333",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#ff690f",
                            },
                          },
                          "& .MuiInputBase-input": {
                            fontSize: "14px",
                            fontWeight: 500,
                            color: "#333",
                          },
                        }}
                      />
                    )}
                    PaperComponent={({ children }) => (
                      <Paper
                        sx={{
                          borderRadius: 3,
                          boxShadow: "0 4px 24px rgba(0,0,0,0.12)",
                          width: 520,
                          p: "16px",
                        }}
                      >
                        {children}
                      </Paper>
                    )}
                    ListboxProps={{
                      id: "from-listbox",
                      sx: {
                        "& .MuiAutocomplete-option": {
                          p: "16px",
                          fontSize: "16px",
                          borderBottom: "1px solid #f0f0f0",
                          '&[aria-selected="true"]': {
                            backgroundColor: "#f3f5f6",
                          },
                          "&:hover": {
                            backgroundColor: "#e6ebef !important",
                            borderRadius: "8px",
                          },
                        },
                        // Hide scrollbar while keeping scrollable
                        "&::-webkit-scrollbar": {
                          display: "none",
                        },
                        "-ms-overflow-style": "none", // IE and Edge
                        "scrollbarWidth": "none", // Firefox
                      },
                    }}
                    multiple
                    disableCloseOnSelect
                    PopperComponent={(props) => (
                      <Popper
                        {...props}
                        style={{ width: 450, zIndex: 1300 }}
                        placement="bottom-start"
                        anchorEl={expandedFromAnchorRef.current}
                      />
                    )}
                  />
                </Box>
              </Box>

              {/* Swap Button */}
              <IconButton
                onClick={handleSwap}
                sx={{
                  maxWidth: "54px",
                  minWidth: "54px",
                  height: "52px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#f0f3f5",
                  borderRadius: "8px",
                  cursor: "pointer",
                }}
              >
                <svg
                  viewBox="0 0 200 200"
                  width="1.25em"
                  height="1.25em"
                  xmlns="http://www.w3.org/2000/svg"
                  class="prSa-icon"
                  role="presentation"
                  style={{
                    transform: isSwapRotating
                      ? "rotate(180deg)"
                      : "rotate(0deg)",
                    transition: "transform 250ms ease",
                  }}
                >
                  <path d="M56.238 154.801c-25.271-30.326-30.335-33.201-25-39.603l25-30l11.523 9.603L53.013 112.5H120v15H53.013l14.749 17.699l-11.524 9.602zm86.524-40l-11.523-9.603L145.987 87.5H80v-15h65.987l-14.749-17.699l11.523-9.603l25 30c5.335 6.403.272 9.278-24.999 39.603z"></path>
                </svg>
              </IconButton>

              {/* To Field */}
              <Box
                sx={{ flex: 1 }}
                onClick={(e) => {
                  e.stopPropagation();
                  closeAllDropdowns();
                  setToFieldOpen(true);
                  setFromFieldOpen(false); // Close From dropdown when To is clicked
                  // Auto-focus the text field immediately
                  setTimeout(() => {
                    try {
                      if (toInputRef.current) {
                        toInputRef.current.focus();
                      } else {
                        // Fallback: try to focus any input in the document
                        const inputs = document.querySelectorAll('input[placeholder="To?"]');
                        if (inputs.length > 0) {
                          inputs[0].focus();
                        }
                      }
                    } catch (error) {
                      console.log('Focus error:', error);
                    }
                  }, 50);
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }} ref={expandedToAnchorRef}>
                  {to.length > 0 ? (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        padding: "0px 16px",
                        backgroundColor: "#f0f3f5",
                        borderRadius: "8px",
                        position: "relative",
                        height: "52px",
                        maxHeight: "52px",
                        border: toFieldOpen ? "1px solid #333333" : "1px solid #d9e2e8",
                        "&:hover": {
                          backgroundColor: "#d9e2e8",
                        },
                      }}
                    >
                      {to.map((city, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            backgroundColor: "#fff",
                            borderRadius: "2px",
                            padding: "1.5px 8px",
                            border: "1px solid #e0e0e0",
                            fontSize: "14px",
                            fontWeight: 500,
                            color: "#333",
                            gap: 1,
                            minWidth: 0,
                            maxWidth: "180px",
                            "&:hover": {
                              borderColor: "#333333",
                            },
                          }}
                        >
                          <Typography
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              fontSize: "14px",
                              fontWeight: 500,
                              color: "#333",
                              flex: 1,
                              minWidth: 0,
                            }}
                          >
                            {to.length === 1
                              ? city
                              : city.split("(")[1]?.replace(")", "") || city}
                          </Typography>
                          <IconButton
                            size="small"
                            sx={{
                              width: "11px",
                              height: "11px",
                              border: "1px solid #d9e2e8",
                              borderRadius: "4px",
                              color: "#192024",
                              fill: "#192024",
                              p: 0,
                              minWidth: "11px",
                              flexShrink: 0,
                              "&:hover": {
                                backgroundColor: "rgba(0,0,0,0.1)",
                              },
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              const newTo = to.filter((_, i) => i !== index);
                              setTo(newTo);
                            }}
                          >
                            <svg
                              width="12"
                              height="12"
                              viewBox="0 0 200 200"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M180 37.918L162.082 20L100 82.208L37.918 20L20 37.918L82.208 100L20 162.082L37.918 180L100 117.792L162.082 180L180 162.082L117.792 100z"
                              />
                            </svg>
                          </IconButton>
                        </Box>
                      ))}
                      {to.length < 3 && (
                        <>
                          {/* Show text field when focused, when there's input, or when plus button is clicked */}
                          {(toFieldOpen || toQuery.length > 0 || isToPlusClick) && (
                            <TextField
                              variant="outlined"
                              value={toQuery}
                              inputRef={toInputRef}
                              autoFocus={isExpanded ? toFieldOpen : isToPlusClick}
                              onFocus={() => {
                                // Don't open dropdown when plus button is clicked
                                if (!isToPlusClick) {
                                  setToFieldOpen(true);
                                  setToQuery("");
                                }
                              }}
                              onClick={() => {
                                // Reset the plus click flag when clicking the field
                                if (isToPlusClick) {
                                  setIsToPlusClick(false);
                                }
                              }}
                              onBlur={() => {
                                // Keep field open if there's text, close if empty
                                if (toQuery.length === 0) {
                                  setTimeout(() => setToFieldOpen(false), 200);
                                }
                              }}
                              sx={{
                                minWidth: "120px",
                                maxWidth: "200px",
                                backgroundColor: "transparent",
                                "& .MuiOutlinedInput-root": {
                                  backgroundColor: "transparent",
                                  height: "26px",
                                  "& fieldset": { 
                                    border: "none",
                                    borderRadius: "2px",
                                  },
                                },
                                "& .MuiInputBase-input": {
                                  p: "4px 8px",
                                  fontSize: "14px",
                                  fontWeight: 500,
                                  color: "#333",
                                  "&::placeholder": {
                                    color: "#999",
                                    opacity: 1,
                                  },
                                },
                              }}
                              placeholder={isToPlusClick ? "" : "To?"}
                              onChange={(e) => {
                                setToQuery(e.target.value);
                                // Only open dropdown if not from plus button click
                                if (!toFieldOpen && !isToPlusClick) setToFieldOpen(true);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" && toQuery.trim()) {
                                  // Handle Enter key to add custom location
                                  const newTo = [...to, toQuery.trim()];
                                  setTo(newTo);
                                  setToQuery("");
                                  setToFieldOpen(false);
                                }
                                if (e.key === "Escape") {
                                  setToQuery("");
                                  setToFieldOpen(false);
                                }
                              }}
                            />
                          )}
                          {/* Show plus button only when text field is not visible */}
                          {!toFieldOpen && toQuery.length === 0 && !isToPlusClick && (
                            <IconButton
                              size="small"
                              sx={{
                                width: "26px",
                                height: "26px",
                                color: "#666",
                                minWidth: "26px",
                                flexShrink: 0,
                                backgroundColor: "#ffffff",
                                border: "1px solid #e0e0e0",
                                borderRadius: "2px",
                                "&:hover": {
                                  backgroundColor: "#ffffff",
                                  borderColor: "#333333",
                                },
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setIsToPlusClick(true);
                                setToFieldOpen(false); // Keep dropdown closed
                                setFromFieldOpen(false); // Close From dropdown
                                setToQuery("");
                                
                                // Focus the text field after it's rendered
                                setTimeout(() => {
                                  if (toInputRef.current) {
                                    toInputRef.current.focus();
                                  } else {
                                    // Fallback: try to find by ID
                                    const inputs = document.getElementById('to-input');
                                    if (inputs) {
                                      inputs.focus();
                                    }
                                  }
                                }, 100);
                                
                                // Additional focus attempt with longer delay
                                setTimeout(() => {
                                  if (toInputRef.current) {
                                    toInputRef.current.focus();
                                  }
                                }, 200);
                              }}
                            >
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 200 200"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path d="M107.5 92.5V30h-15v62.5H30v15h62.5V170h15v-62.5H170v-15z" />
                              </svg>
                            </IconButton>
                          )}
                        </>
                      )}
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        padding: "0px 16px",
                        backgroundColor: "#f0f3f5",
                        borderRadius: "8px",
                        height: "52px",
                        maxHeight: "52px",
                        border: toFieldOpen ? "1px solid #333333" : "1px solid #d9e2e8",
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor: "#d9e2e8",
                          borderColor: "#333333",
                        },
                      }}
                    >
                      <svg
                        viewBox="0 0 200 200"
                        width="1.25em"
                        height="1.25em"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{ marginRight: "8px", color: "#666" }}
                      >
                        <path d="M178.081 41.973c-2.681 2.663-16.065 17.416-28.956 30.221c0 107.916 3.558 99.815-14.555 117.807l-14.358-60.402l-14.67-14.572c-38.873 38.606-33.015 8.711-33.015 45.669c.037 8.071-3.373 13.38-8.263 18.237L50.66 148.39l-30.751-13.513c10.094-10.017 15.609-8.207 39.488-8.207c8.127-16.666 18.173-23.81 26.033-31.62L70.79 80.509L10 66.269c17.153-17.039 6.638-13.895 118.396-13.895c12.96-12.873 26.882-27.703 29.574-30.377c7.745-7.692 28.017-14.357 31.205-11.191c3.187 3.166-3.349 23.474-11.094 31.167zm-13.674 42.469l-8.099 8.027v23.58c17.508-17.55 21.963-17.767 8.099-31.607zm-48.125-47.923c-13.678-13.652-12.642-10.828-32.152 8.57h23.625l8.527-8.57z" />
                      </svg>
                      <TextField
                        variant="outlined"
                        value={toQuery}
                        inputRef={toInputRef}
                        autoFocus={isExpanded ? toFieldOpen : isToPlusClick}
                                                  onFocus={() => {
                            // Don't open dropdown when plus button is clicked
                            if (!isToPlusClick) {
                              setToFieldOpen(true);
                            }
                            // Reset the flag after focus
                            setTimeout(() => {
                              setIsToPlusClick(false);
                            }, 200);
                          }}
                          onClick={() => {
                            // Reset the plus click flag when clicking the field
                            if (isToPlusClick) {
                              setIsToPlusClick(false);
                            }
                          }}
                        sx={{
                          flex: 1,
                          backgroundColor: "transparent",
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "transparent",
                            px: 0,
                            '& fieldset': { border: 'none' },
                            '&:hover fieldset': { border: 'none' },
                            '&.Mui-focused fieldset': { border: 'none' },
                          },
                          "& .MuiInputBase-input": {
                            p: 0,
                            fontSize: "16px",
                            fontWeight: 500,
                            color: "#999",
                          },
                          
                        }}
                        placeholder={isToPlusClick ? "" : "To?"}
                        onChange={(e) => {
                          setToQuery(e.target.value);
                          // Only open dropdown if not from plus button click
                          if (!toFieldOpen && !isToPlusClick) setToFieldOpen(true);
                        }}
                        id="to-input"
                      />
                    </Box>
                  )}
                </Box>

                {/* Hidden Autocomplete for functionality */}
                <Box
                  sx={{
                    position: "absolute",
                    opacity: 0,
                    pointerEvents: "none",
                  }}
                >
                  <Autocomplete
                    options={getGroupedCities()}
                    getOptionLabel={(option) =>
                      option
                        ? option.displayname ||
                        `${option.city}, ${option.country} ${option.code}`
                        : ""
                    }
                    open={toFieldOpen}
                    onOpen={() => {
                      closeAllDropdowns();
                      setToFieldOpen(true);
                    }}
                    onClose={() => setToFieldOpen(false)}
                    value={to.map(
                      (t) =>
                        getGroupedCities().find(
                          (c) => `${c.city} (${c.code})` === t
                        ) || null
                    )}
                    inputValue={toQuery}
                    onInputChange={(event, newInputValue, reason) => {
                      if (reason === 'input') {
                        setToQuery(newInputValue);
                      }
                    }}
                    filterOptions={(options, params) => {
                      const filtered = defaultFilter(options, params);
                      const currentInput = (params && typeof params.inputValue === 'string' ? params.inputValue : toQuery) || "";
                      const isEmptyInput = currentInput.trim() === "";
                      const curatedCodesArr = ["NYC", "DEL", "LAX", "LAS", "HNL"]; // curated set

                      // Don't show curated list if "Anywhere" is in the input
                      if (currentInput === "Anywhere") {
                        return [];
                      }

                      if (isEmptyInput && to.length === 0) {
                        // Show ONLY curated image items + CTA when empty
                        const curated = options.filter(
                          (o) => curatedCodesArr.includes(o.code) && !o.sub
                        );
                        return [...curated, { code: "ANY_CTA", city: "Anywhere CTA" }];
                      }
                      return filtered;
                    }}
                    onChange={(_, newValue) => {
                      // Handle "Anywhere" as text input, not as chip
                      if (toQuery === "Anywhere") {
                        setToFieldOpen(false);
                        setTimeout(() => {
                          setDatePopoverAnchorLocal(searchBarRefLocal.current);
                          setActiveDateFieldLocal("departure");
                        }, 300);
                        return;
                      }

                      // Handle single selection from dropdown
                      if (newValue && newValue.length > 0) {
                        const lastSelected = newValue[newValue.length - 1];
                        if (lastSelected && lastSelected.city && lastSelected.code) {
                          const newToValues = [...to, `${lastSelected.city} (${lastSelected.code})`];
                          setTo(newToValues);
                          setToQuery("");
                          
                          // Close dropdown after selection
                          setToFieldOpen(false);
                          
                          // Auto-open date picker only after FIRST selection (when to was empty)
                          if (to.length === 0) {
                            setTimeout(() => {
                              setDatePopoverAnchorLocal(searchBarRefLocal.current);
                              setActiveDateFieldLocal("departure");
                            }, 300);
                          }
                        }
                      } else {
                        setToFieldOpen(false);
                      }
                    }}
                    renderOption={(props, option) => {
                      if (!option) return null; // Early return if option is null
                      if (option.code === "ANY_CTA") {
                        // Special CTA row
                        return (
                          <li
                            {...props}
                            onClick={(e) => {
                              e.preventDefault();
                              // Set "Anywhere" as text input instead of adding as chip
                              setToQuery("Anywhere");
                              setToFieldOpen(false);
                              setTimeout(
                                () => setDatePopoverAnchor(searchBarRef.current),
                                200
                              );
                            }}
                            style={{ listStyle: "none", width: "100%", p: "0px !important" }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                                px: 2,
                                py: 1.5,
                                borderRadius: 2,
                                backgroundColor: "transparent",
                                color: "#000",
                                justifyContent: "space-between",
                                transition: "background-color 0.2s ease",
                                '&:hover': {
                                  backgroundColor: '#079',
                                  color: "#fff !important",
                                },
                                width: "100%",
                              }}
                            >
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                <Box
                                  sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: 2,
                                    backgroundColor: "#f0f0f0",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <svg viewBox="0 0 200 200" width="32" height="32" xmlns="http://www.w3.org/2000/svg" role="presentation">
                                    <path d="M150 70l20 30h-50c-13.24 0-13.221-20 0-20H60V20h60v20h50l-20 30zM30 35.007V180h20V20h-4.993C36.719 20 30 26.719 30 35.007z" fill="#666" />
                                  </svg>
                                </Box>
                                <Box>
                                  <Typography sx={{ fontWeight: 700, color: "inherit" }}>
                                    Can't decide where to go?
                                  </Typography>
                                  <Typography sx={{ fontSize: 13, color: "inherit", opacity: 0.8 }}>
                                    Explore your options with anywhere search
                                  </Typography>
                                </Box>
                              </Box>
                              <Box>
                                <svg viewBox="0 0 200 200" width="1.25em" height="1.25em" xmlns="http://www.w3.org/2000/svg" role="presentation">
                                  <path d="M142.762 134.801l-11.523-9.603l14.749-17.699H30v-15h115.987L131.238 74.8l11.523-9.603l25 30c5.335 6.404.272 9.279-24.999 39.604z" fill="currentColor" />
                                </svg>
                              </Box>
                            </Box>
                          </li>
                        );
                      }
                      const isSelected = to.some(
                        (t) => t === `${option.city} (${option.code})`
                      );
                      const isDisabled = !isSelected && to.length >= 3;
                      const isSubAirport = option.sub;
                      const showImageRow = to.length === 0;

                      const getCuratedImage = (code) => {
                        const imageMap = {
                          "NYC": "https://content.r9cdn.net/rimg/dimg/8c/a9/d1b21b20-city-15830-16eb6a60801.jpg?width=128&height=128&xhint=1887&yhint=913&outputtype=JPEG&crop=true",
                          "DEL": "https://content.r9cdn.net/rimg/dimg/6d/77/0cc45283-city-32821-176ddb032b7.jpg?width=128&height=128&xhint=1769&yhint=1931&outputtype=JPEG&crop=true",
                          "LAX": "https://content.r9cdn.net/rimg/dimg/a6/ae/d1a4a522-city-16078-190266aa26e.jpg?width=128&height=128&xhint=1430&yhint=902&outputtype=JPEG&crop=true",
                          "LAS": "https://content.r9cdn.net/rimg/dimg/0a/9e/073516d2-city-35107-1656d23229c.jpg?width=128&height=128&xhint=2420&yhint=1089&outputtype=JPEG&crop=true",
                          "HNL": "https://content.r9cdn.net/rimg/dimg/73/93/010fa630-city-28070-15b0fbf9066.jpg?width=128&height=128&xhint=1898&yhint=1299&outputtype=JPEG&crop=true"
                        };
                        return imageMap[code] || "https://via.placeholder.com/56";
                      };

                      return (
                        <li
                          {...props}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            padding: isSubAirport ? "8px 12px 8px 48px" : "12px",
                            borderBottom: "1px solid #f0f0f0",
                            opacity: isDisabled ? 0.5 : 1,
                            cursor: isDisabled ? "not-allowed" : "pointer",
                            backgroundColor: "#fff",
                          }}
                        >
                          <span
                            style={{
                              marginRight: 12,
                              color: "#666",
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            {isSubAirport && (
                              <SubdirectoryArrowRightIcon
                                sx={{
                                  fontSize: "28px",
                                  fontWeight: 900,
                                  color: "#333333",
                                }}
                              />
                            )}
                            {showImageRow && to.length === 0 && ["NYC", "DEL", "LAX", "LAS", "HNL"].includes(option.code) ? (
                              <img
                                src={getCuratedImage(option.code)}
                                alt="thumb"
                                style={{
                                  width: 56,
                                  height: 56,
                                  borderRadius: 8,
                                  objectFit: "cover",
                                }}
                              />
                            ) : (
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1, backgroundColor: "#f0f3f5", fill: "#5a6872", p: 2, borderRadius: 2 }}>
                                <img
                                  src="/614.png"
                                  alt="airplane"
                                  style={{
                                    width: "24px",
                                    height: "24px",
                                    objectFit: "contain",
                                  }}
                                />
                              </Box>
                            )}
                          </span>
                          <div style={{ flex: 1 }}>
                            <div
                              style={{
                                fontWeight: isSubAirport ? 400 : 600,
                                fontSize: "16px",
                                color: "#333",
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                const optionValue = `${option.city} (${option.code})`;
                                if (isSelected) {
                                  setTo(to.filter((t) => t !== optionValue));
                                } else if (to.length < 3) {
                                  setTo([...to, optionValue]);
                                  setToQuery("");
                                }
                                setToFieldOpen(false);
                              }}
                            >
                              {isSubAirport
                                ? option.name
                                : `${option.city}, ${option.region || option.country
                                }, ${option.country}`}{" "}
                              <span style={{ color: "#666", fontWeight: 400 }}>
                                {option.code}
                              </span>
                            </div>
                            <div
                              style={{
                                fontSize: "14px",
                                color: "#666",
                                marginTop: 2,
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                const optionValue = `${option.city} (${option.code})`;
                                if (isSelected) {
                                  setTo(to.filter((t) => t !== optionValue));
                                } else if (to.length < 3) {
                                  setTo([...to, optionValue]);
                                  setToQuery("");
                                }
                                setToFieldOpen(false);
                              }}
                            >
                              {isSubAirport
                                ? `${option.city}, ${option.region || option.country
                                }, ${option.country}`
                                : option.isMetroOnly
                                  ? "All airports"
                                  : option.name}
                            </div>
                          </div>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            disabled={isDisabled}
                            onChange={(e) => {
                              e.stopPropagation();
                              const optionValue = `${option.city} (${option.code})`;
                              if (isSelected) {
                                setTo(to.filter((t) => t !== optionValue));
                              } else if (to.length < 3) {
                                setTo([...to, optionValue]);
                                setToQuery("");
                              }
                            }}
                            style={{
                              margin: 0,
                              width: "16px",
                              height: "16px",
                              cursor: isDisabled ? "not-allowed" : "pointer",
                            }}
                          />
                        </li>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="To?"
                        variant="outlined"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "#f0f3f5",
                            borderRadius: "8px",
                            height: "52px",
                            "& fieldset": {
                              border: "1px solid #d9e2e8",
                            },
                            "&:hover fieldset": {
                              borderColor: "#333333",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#ff690f",
                            },
                          },
                          "& .MuiInputBase-input": {
                            fontSize: "14px",
                            fontWeight: 500,
                            color: "#333",
                          },
                        }}
                      />
                    )}
                    renderTags={(props, { inputProps, rootProps }) => (
                      <Box {...rootProps}>
                        <Box {...inputProps} />
                      </Box>
                    )}
                    PaperComponent={({ children }) => (
                      <Paper
                        sx={{
                          borderRadius: 3,
                          boxShadow: "ss0 4px 24px rgba(0,0,0,0.12)",
                          width: 520,
                          p: "16px",
                        }}
                      >
                        {children}
                      </Paper>
                    )}
                    ListboxProps={{
                      id: "to-listbox",
                      sx: {
                        "& .MuiAutocomplete-option": {
                          p: "16px",
                          fontSize: "16px",
                          borderBottom: "1px solid #f0f0f0",
                          '&[aria-selected="true"]': {
                            backgroundColor: "#f3f5f6",
                          },
                          "&:hover": {
                            backgroundColor: "#e6ebef !important",
                            borderRadius: "8px",
                          },
                        },
                        // Hide scrollbar while keeping scrollable
                        "&::-webkit-scrollbar": {
                          display: "none",
                        },
                        "-ms-overflow-style": "none", // IE and Edge
                        "scrollbarWidth": "none", // Firefox
                      },
                    }}
                    multiple
                    disableCloseOnSelect
                    PopperComponent={(props) => (
                      <Popper
                        {...props}
                        style={{ width: 450, zIndex: 1300 }}
                        placement="bottom-start"
                        anchorEl={expandedToAnchorRef.current}
                      />
                    )}
                  />
                </Box>
              </Box>
            </Box>

            {/* Date Range Container */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                backgroundColor: "#f0f3f5",
                borderRadius: "8px",
                cursor: "pointer",
                "&:hover": {
                  borderColor: "#ff690f",
                },
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleFieldClick("dates");
                setToFieldOpen(false);
              }}
            >
              {/* Departure Date */}
              <Box
                onClick={(e) => {
                  e.stopPropagation();
                  setDatePopoverAnchorLocal(expandedDepartureDateRef.current || e.currentTarget);
                  setActiveDateFieldLocal("departure");
                  setToFieldOpen(false);
                }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  padding: "0px 12px",
                  height: "52px",
                  maxHeight: "52px",
                  borderRadius: "8px",
                  backgroundColor: "#f0f3f5",
                  cursor: "pointer",
                  minHeight: "28px",
                  whiteSpace: "nowrap",
                  "&:hover": {
                    backgroundColor: "#d9e2e8",
                    borderRadius: "8px",
                  },
                }}
                ref={expandedDepartureDateRef}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 200 200"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ marginRight: "8px", color: "#666" }}
                >
                  <path
                    fill="#333333"
                    d="M185 33.182v30.909H15V33.182h30.909V10h23.182v23.182h61.818V10h23.182v23.182H185zM15 71.818h170v92.727c0 8.535-6.919 15.455-15.455 15.455H30.455C21.919 180 15 173.081 15 164.545V71.818zm115.909 46.364h30.909v-30.91h-30.909v30.91zm-46.364 0h30.909v-30.91H84.545v30.91zm0 46.363h30.909v-30.909H84.545v30.909zm-46.363-46.363h30.909v-30.91H38.182v30.91zm0 46.363h30.909v-30.909H38.182v30.909z"
                  />
                </svg>
                <Typography
                  sx={{ fontSize: "14px", fontWeight: 500, color: "#333" }}
                >
                  {formatDate(departureDate)}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    ml: 1,
                    visibility: isDatePickerOpen() ? "hidden" : "visible",
                  }}
                >
                  <IconButton
                    size="small"
                    sx={{ width: 16, height: 16, color: "#666", p: 0 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDateNavigation(-1, "departure");
                    }}
                  >
                    <svg
                      viewBox="0 0 200 200"
                      width="16"
                      height="16"
                      xmlns="http://www.w3.org/2000/svg"
                      role="presentation"
                    >
                      <path
                        fill="#666"
                        d="M119.238 164.801c-53.425-64.11-55.449-63.064-50-69.603l50-60l11.523 9.603L84.763 100l45.999 55.199l-11.524 9.602z"
                      />
                    </svg>
                  </IconButton>
                  <IconButton
                    size="small"
                    sx={{ width: 16, height: 16, color: "#666", p: 0 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDateNavigation(1, "departure");
                    }}
                  >
                    <svg
                      viewBox="0 0 200 200"
                      width="16"
                      height="16"
                      xmlns="http://www.w3.org/2000/svg"
                      role="presentation"
                      style={{ transform: "rotate(180deg)" }}
                    >
                      <path
                        fill="#666"
                        d="M119.238 164.801c-53.425-64.11-55.449-63.064-50-69.603l50-60l11.523 9.603L84.763 100l45.999 55.199l-11.524 9.602z"
                      />
                    </svg>
                  </IconButton>
                </Box>
              </Box>

              {/* Return Date - Only show if not One-way */}
              {tripType !== "One-way" && (
                <Box
                  onClick={(e) => {
                    e.stopPropagation();
                    setDatePopoverAnchorLocal(expandedReturnDateRef.current || e.currentTarget);
                    setActiveDateFieldLocal("return");
                    setToFieldOpen(false);
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    padding: "12px 12px",
                    borderRadius: "8px",
                    backgroundColor: "#f0f3f5",
                    cursor: "pointer",
                    minHeight: "28px",
                    whiteSpace: "nowrap",
                    "&:hover": {
                      backgroundColor: "#d9e2e8",
                      borderRadius: "8px",
                    },
                  }}
                  ref={expandedReturnDateRef}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 200 200"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{ marginRight: "8px", color: "#666" }}
                  >
                    <path
                      fill="#333333"
                      d="M185 33.182v30.909H15V33.182h30.909V10h23.182v23.182h61.818V10h23.182v23.182H185zM15 71.818h170v92.727c0 8.535-6.919 15.455-15.455 15.455H30.455C21.919 180 15 173.081 15 164.545V71.818zm115.909 46.364h30.909v-30.91h-30.909v30.91zm-46.364 0h30.909v-30.91H84.545v30.91zm0 46.363h30.909v-30.909H84.545v30.909zm-46.363-46.363h30.909v-30.91H38.182v30.91zm0 46.363h30.909v-30.909H38.182v30.909z"
                    />
                  </svg>
                  <Typography
                    sx={{ fontSize: "14px", fontWeight: 500, color: "#333" }}
                  >
                    {formatDate(returnDate)}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      ml: 1,
                      visibility: isDatePickerOpen() ? "hidden" : "visible",
                    }}
                  >
                    <IconButton
                      size="small"
                      sx={{
                        width: 16,
                        height: 16,
                        color: isReturnLeftArrowDisabled() ? "#ccc" : "#333333",
                        p: 0,
                        cursor: isReturnLeftArrowDisabled()
                          ? "not-allowed"
                          : "pointer",
                      }}
                      disabled={isReturnLeftArrowDisabled()}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!isReturnLeftArrowDisabled()) {
                          handleDateNavigation(-1, "return");
                        }
                      }}
                    >
                      <svg
                        viewBox="0 0 200 200"
                        width="16"
                        height="16"
                        xmlns="http://www.w3.org/2000/svg"
                        role="presentation"
                      >
                        <path
                          fill={
                            isReturnLeftArrowDisabled() ? "#ccc" : "#333333"
                          }
                          d="M119.238 164.801c-53.425-64.11-55.449-63.064-50-69.603l50-60l11.523 9.603L84.763 100l45.999 55.199l-11.524 9.602z"
                        />
                      </svg>
                    </IconButton>
                    <IconButton
                      size="small"
                      sx={{ width: 16, height: 16, color: "#666", p: 0 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDateNavigation(1, "return");
                      }}
                    >
                      <svg
                        viewBox="0 0 200 200"
                        width="16"
                        height="16"
                        xmlns="http://www.w3.org/2000/svg"
                        role="presentation"
                        style={{ transform: "rotate(180deg)" }}
                      >
                        <path
                          fill="#666"
                          d="M119.238 164.801c-53.425-64.11-55.449-63.064-50-69.603l50-60l11.523 9.603L84.763 100l45.999 55.199l-11.524 9.602z"
                        />
                      </svg>
                    </IconButton>
                  </Box>
                </Box>
              )}
            </Box>

            {/* Travelers & Cabin */}
            <Box
              data-travelers-field
              ref={expandedTravelersRef}
              onClick={(e) => {
                e.stopPropagation();
                closeAllDropdowns();
                setPassengersAnchorEl(expandedTravelersRef.current || e.currentTarget);
                handleFieldClick("passengers");
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                padding: "8px 12px",
                borderRadius: "8px",
                backgroundColor: "#f0f3f5",
                border: "1px solid #e0e0e0",
                cursor: "pointer",
                whiteSpace: "nowrap",
                minHeight: "28px",
                gap: 1,
                "&:hover": {
                  backgroundColor: "#d9e2e8",
                  borderRadius: "8px",
                },
              }}
            >
              <svg viewBox="0 0 200 200" width="20" height="20" xmlns="http://www.w3.org/2000/svg" class="uVPW-icon" role="presentation"><path d="M180 155.435V177.5H20v-22.064c50.314-24.61 110.67-24.128 160-.001zm-80-36.132c17.673 0 32-14.446 32-32.267V54.768c0-17.821-14.327-32.268-32-32.268S68 36.946 68 54.768v32.268c0 17.82 14.327 32.267 32 32.267z"></path></svg>
              <Typography
                sx={{ fontSize: "14px", fontWeight: 500, color: "#333" }}
              >
                {adults +
                  students +
                  seniors +
                  youths +
                  childrenCount +
                  toddlers +
                  infants}{" "}
                {totalTravelers === 1 && adults === 1 ? 'adult' : 'travelers'}, {cabin}
              </Typography>
            </Box>

            {/* Search Button */}
            <Button
              variant="contained"
              onClick={handleSubmit}
              sx={{
                background: "linear-gradient(135deg, #ff690f 0%, #e8381b 100%)",
                borderRadius: "8px",
                minWidth: "120px",
                minHeight: "48px",
                boxShadow: "0 4px 12px rgba(255, 105, 15, 0.3)",
                textTransform: "none",
                fontSize: "16px",
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 3,
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #e65c00 0%, #d13017 100%)",
                  boxShadow: "0 6px 16px rgba(255, 105, 15, 0.4)",
                },
              }}
            >
              <svg
                viewBox="0 0 200 200"
                width="20"
                height="20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fill="#fff"
                  d="M174.973 150.594l-29.406-29.406c5.794-9.945 9.171-21.482 9.171-33.819C154.737 50.164 124.573 20 87.368 20S20 50.164 20 87.368s30.164 67.368 67.368 67.368c12.345 0 23.874-3.377 33.827-9.171l29.406 29.406c6.703 6.703 17.667 6.703 24.371 0c6.704-6.702 6.704-17.674.001-24.377zM36.842 87.36c0-27.857 22.669-50.526 50.526-50.526s50.526 22.669 50.526 50.526s-22.669 50.526-50.526 50.526s-50.526-22.669-50.526-50.526z"
                />
              </svg>
              {isResultPage ? "Update" : ""}
            </Button>
          </Box>

          {/* Show "Compare vs. KAYAK" and checkboxes only when more than 2 locations are selected and isExpanded is true */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mt: 0.5,
                mb: 1,
              }}
            >
              {(hasMoreThanTwoLocations() || ['Premium Economy','Business','First'].includes(cabin)) && isExpanded && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
                <Typography
                  variant="body2"
                  sx={{ color: "#666", fontSize: "14px" }}
                >
                  Compare vs. KAYAK
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={includeEmirates}
                      onChange={(e) => setIncludeEmirates(e.target.checked)}
                      size="small"
                    />
                  }
                  label={
                    <Typography
                      variant="body2"
                      sx={{ fontSize: "14px", color: "#333" }}
                    >
                      Emirates
                    </Typography>
                  }
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={includePriceline}
                      onChange={(e) => setIncludePriceline(e.target.checked)}
                      size="small"
                    />
                  }
                  label={
                    <Typography
                      variant="body2"
                      sx={{ fontSize: "14px", color: "#333" }}
                    >
                      Priceline
                    </Typography>
                  }
                />
              </Box>
)}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={directOnly}
                    onChange={(e) => setDirectOnly(e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "14px", color: "#333", m: 0 }}
                  >
                    Direct flights only
                  </Typography>
                }
                sx={{ml: "auto"}}
              />
            </Box>

        
        </Box>
      </Box>

      {/* Date Picker Popover */}
      <DatePickerPopover
        anchorEl={datePopoverAnchorLocal}
        open={Boolean(datePopoverAnchorLocal)}
        onClose={closeDatePicker}
        currentMonth={currentMonthLocal}
        setCurrentMonth={setCurrentMonthLocal}
        dateTab={dateTabLocal}
        setDateTab={setDateTabLocal}
        departureExact={departureExactLocal}
        setDepartureExact={setDepartureExactLocal}
        returnExact={returnExactLocal}
        setReturnExact={setReturnExactLocal}
        tripType={tripType}
        departureDate={departureDate}
        setDepartureDate={setDepartureDate}
        returnDate={returnDate}
        setReturnDate={setReturnDate}
        handleDateSelect={handleDateSelect}
        isDateSelected={isDateSelected}
        isDateInRange={isDateInRange}
        formatDate={formatDate}
        generateCalendarDates={generateCalendarDates}
        generatePriceForDate={generatePriceForDate}
        getPriceColor={getPriceColor}
        getPriceBackgroundColor={getPriceBackgroundColor}
        showPrices={from.length > 0 && from[0] && to.length > 0 && to[0]}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        activeDateField={activeDateFieldLocal}
      />

      {/* Passenger Menu */}
      <PassengerMenu
        anchorEl={passengersAnchorEl}
        open={Boolean(passengersAnchorEl)}
        onClose={() => setPassengersAnchorEl(null)}
        adults={adults}
        setAdults={setAdults}
        students={students}
        setStudents={setStudents}
        seniors={seniors}
        setSeniors={setSeniors}
        youths={youths}
        setYouths={setYouths}
        childrenCount={childrenCount}
        setChildrenCount={setChildrenCount}
        toddlers={toddlers}
        setToddlers={setToddlers}
        infants={infants}
        setInfants={setInfants}
        cabin={cabin}
        setCabin={setCabin}
        flightSegments={[]}
        setFlightSegments={() => { }}
        isResultPage={isResultPage}
        isExpanded={isExpanded}
      />
    </Box>
  );
} 
